import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { countBy, isEmpty, mapValues, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types';
import { TableList, Page, PageEmptyState, PageErrorState, Button, Typography, PageContentHeader, ConfirmationModal, DropdownMenu, DropdownMenuItem, ChipButton, useStateInRef } from 'seagull';
import { ChevronDown, ChevronUp, Download, EmailSent, Import } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import AgreementStudioPage from '~/components/AgreementStudioPage/AgreementStudioPage';
import { useFetchAgreementStudioData } from '~/components/AgreementStudioPage/hooks/useFetchAgreementStudioData';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useProposals } from '~/hooks/reactQuery/queries/useProposals';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useProposalActions } from '~/hooks/useProposalActions';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { relationshipSummaryModel } from '~/models';
import { DraftModel } from '~/models/DraftModel';
import { RelationshipStatus } from '~/models/relationship';
import { useBusinessChannelEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import ActivityLogModal from './ActivityLogModal/ActivityLogModal';
import styles from './ProposalsDashboard.module.scss';
import { sortFunctions, getStatusUIData, getProposalsStatusCounters, proposalStatusDefinition } from './ProposalsDashboard.utils';
import ProposalsDashboardRow from './ProposalsDashboardRow';
import { ProposalsIllustration } from './ProposalsIllustration';
import img1 from './SeeWhatItsLikeImages/1.png';
import img2 from './SeeWhatItsLikeImages/2.png';
import img3 from './SeeWhatItsLikeImages/3.png';
import img4 from './SeeWhatItsLikeImages/4.png';
import { WithdrawModal } from './WithdrawModal/WithdrawModal';
export var HeaderColId = /*#__PURE__*/ function(HeaderColId) {
    HeaderColId["CompanyName"] = "companyName";
    HeaderColId["SentDate"] = "sentDate";
    HeaderColId["SentBy"] = "sentBy";
    HeaderColId["Status"] = "status";
    return HeaderColId;
}({});
var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["Status"] = "status";
    FilterType["SentBy"] = "sentBy";
    return FilterType;
}(FilterType || {});
const getTableDefs = (texts)=>{
    const sentDateLabel = texts.columnHeaders.sentDate();
    const sentByLabel = texts.columnHeaders.sentBy();
    return [
        {
            headerItem: {
                id: "companyName",
                value: texts.columnHeaders.companyName()
            },
            columnDef: {
                width: 300,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_proposals_by_company_name'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "sentDate",
                value: sentDateLabel
            },
            columnDef: {
                width: [
                    90,
                    110
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: sentDateLabel
                    }
                },
                bi: {
                    name: 'sort_proposals_by_sent_date'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "sentBy",
                value: sentByLabel
            },
            columnDef: {
                width: [
                    150,
                    180
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: sentByLabel
                    }
                },
                bi: {
                    name: 'sort_proposals_by_sent_by'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "status",
                value: texts.columnHeaders.status()
            },
            columnDef: {
                width: [
                    230,
                    300
                ],
                sortable: true,
                sortOrder: [
                    'asc',
                    'desc'
                ],
                mobile: {
                    inline: true
                },
                bi: {
                    name: 'sort_proposals_by_status'
                }
            },
            defaultSortDirection: 'asc'
        }
    ];
};
const defaultSort = {
    colIndex: 1,
    sort: 'desc'
};
export const GetProposalsDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ProposalsDashboardTID: {
                Empty: 'proposals-dashboard-empty',
                EmptyNewButton: 'agreements-empty-new-btn',
                CloseImportModalButton: 'close-import-proposals-modal',
                MoreActionsButton: 'proposals-dashboard-more-actions-btn',
                ImportButton: 'import-proposals-btn',
                ExportAllButton: 'export-all-proposals-btn',
                BulkCounter: 'agreement-dashboard-bulk-counter',
                BulkActions: 'proposals-dashboard-bulk-actions',
                BulkSendReminders: 'proposals-dashboard-bulk-send-reminders',
                BulkExportCsv: 'proposals-dashboard-bulk-export-csv'
            }
        }
    });
export function ActualProposalsDashboard(props) {
    const { data, isRefreshing, refresh } = props;
    const { ProposalsDashboardTID } = useMemo(()=>GetProposalsDashboardTID(), []);
    const { t: proposalsTexts } = useLocaleKeys('pages.proposalsDashboard');
    const { t: importModalTexts } = useLocaleKeys('pages.proposalsDashboard.importProposalsModal');
    const { t: errorTexts } = useLocaleKeys('errors');
    const isVendor = useStore(selectors.user.isVendor);
    const overlayContext = useOverlayContext();
    const { api, logBI } = useStoreActions();
    const { browser: { downloadCsv } } = useIoContext();
    const fetchAgreementStudioData = useFetchAgreementStudioData();
    const { getState } = useStoreStatic();
    const scrollElRef = useRef(null);
    const [viewProposalMode, setViewProposalMode] = useState(null);
    const defs = useMemo(()=>getTableDefs(proposalsTexts), []);
    const filterLabels = useMemo(()=>({
            ["status"]: proposalsTexts.proposalStatusFilter(),
            ["sentBy"]: proposalsTexts.sentByFilter()
        }), []);
    const headerItems = useMemo(()=>defs.map((d)=>d.headerItem), [
        defs
    ]);
    const columnDefs = useMemo(()=>defs.map((d)=>d.columnDef), [
        defs
    ]);
    const sortIds = useMemo(()=>defs.map((d)=>d.headerItem.id), [
        defs
    ]);
    const sortOptions = useMemo(()=>defs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
                {
                    id: d.headerItem.id,
                    label: String(d.headerItem.value),
                    defaultDirection: d.defaultSortDirection,
                    colIndex
                }
            ] : []), [
        defs
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const actions = useProposalActions();
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11303);
    const { doBulkAction } = useBulkActionModal();
    const isMobileDevice = useIsMobileForHomePage();
    const { urlState: { filter, search, sentBy }, changeUrlState } = useStateFromUrl({
        filter: {
            multi: true,
            defaultValue: []
        },
        search: {
            defaultValue: ''
        },
        // people names may contain commas, so we use a custom query parameter delimeter
        sentBy: {
            multi: true,
            defaultValue: [],
            delimeter: '|'
        }
    });
    const sortedAndFiltered = useMemo(()=>{
        const textSearchProposal = (search, proposal)=>"".concat(relationshipSummaryModel.opposingPartyName(proposal, isVendor), " ").concat(proposal.title).toLowerCase().includes(search);
        const filtered = data.filter((proposal)=>{
            const passesStatusFilter = filter.length === 0 || filter.includes(getStatusUIData(proposal, isVendor, proposalsTexts).viewableStatus);
            const passesSentByFilter = sentBy.length === 0 || sentBy.includes(proposal.seller.id);
            const normalizedSearch = search ? search.trim().toLowerCase() : search;
            const passesSearch = !normalizedSearch || textSearchProposal(normalizedSearch, proposal);
            return passesStatusFilter && passesSentByFilter && passesSearch;
        });
        if (sort) filtered.sort(sortFunctions[sort.colIndex]({
            isVendor,
            isAscending: sort.sort === 'asc',
            proposalsTexts
        }));
        return filtered;
    }, [
        data,
        isVendor,
        sort,
        filter,
        search,
        sentBy
    ]);
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const [exportingAllCsv, setExportingAllCsv] = useState(false);
    const [bulkExportingCsv, setBulkExportingCsv] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFiltered,
        scrollElRef
    });
    const counters = useMemo(()=>getProposalsStatusCounters(data, isVendor, proposalsTexts), [
        data,
        isVendor
    ]);
    const statusOptions = useMemo(()=>{
        const statusDefinition = proposalStatusDefinition(isVendor, proposalsTexts);
        const availableStatuses = Object.keys(counters);
        return Object.keys(statusDefinition).filter((status)=>availableStatuses.indexOf(status) > -1).sort((a, b)=>statusDefinition[b].severity - statusDefinition[a].severity).map((statusName)=>({
                label: statusDefinition[statusName].statusText,
                value: statusName,
                counter: counters[statusName]
            }));
    }, [
        isVendor,
        counters
    ]);
    const sentByOptions = useMemo(()=>{
        const sellerMap = data.reduce((acc, param)=>{
            let { seller: { id, firstName, lastName } } = param;
            return firstName != null && lastName != null ? Object.assign(acc, {
                [id]: "".concat(firstName, " ").concat(lastName)
            }) : acc;
        }, {});
        return Object.entries(sellerMap).sort((param, param1)=>{
            let [, a] = param, [, b] = param1;
            return a.localeCompare(b);
        }).map((param)=>{
            let [id, name] = param;
            return {
                label: name,
                value: id
            };
        });
    }, [
        data
    ]);
    const tableFilterConfig = useMemo(()=>({
            ["status"]: {
                type: SearchFilterType.MultiSelectFilter,
                options: statusOptions,
                removable: false
            },
            ["sentBy"]: {
                type: SearchFilterType.MultiSelectFilter,
                options: sentByOptions,
                removable: false
            }
        }), [
        statusOptions,
        sentByOptions
    ]);
    const tableSelectedFilters = useMemo(()=>({
            ["status"]: filter,
            ["sentBy"]: sentBy
        }), [
        filter,
        sentBy
    ]);
    const handleTableFiltersRefresh = useCallback((newFilters, newSort)=>{
        var _newFilters_status, _newFilters_sentBy;
        changeUrlState({
            filter: (_newFilters_status = newFilters["status"]) !== null && _newFilters_status !== void 0 ? _newFilters_status : [],
            sentBy: (_newFilters_sentBy = newFilters["sentBy"]) !== null && _newFilters_sentBy !== void 0 ? _newFilters_sentBy : []
        });
        if (newSort && (newSort.colIndex !== (sort === null || sort === void 0 ? void 0 : sort.colIndex) || newSort.sort !== (sort === null || sort === void 0 ? void 0 : sort.sort))) {
            changeSort(newSort);
        }
    }, [
        changeSort,
        sort
    ]);
    const onSortChange = (sort)=>{
        logBI(11300, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        changeSort(sort);
    };
    const resetFilters = ()=>{
        changeUrlState({
            filter: [],
            search: '',
            sentBy: []
        });
    };
    useEffect(()=>{
        // this unselects any agreement that is not found in the newly fetched list of agreements
        if (!data || isEmpty(getSelected())) return;
        const ids = data.map((proposal)=>proposal.id);
        const newSelected = mapValues(getSelected(), (origValue, proposalId)=>ids.includes(proposalId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    function getSelectedProposals() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [proposalId, _] = param;
            return proposalId;
        });
    }
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(sortedAndFiltered.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        sortedAndFiltered
    ]);
    if (!data.length) {
        return /*#__PURE__*/ _jsx(Page, {
            header: isVendor ? proposalsTexts.vendorTitle() : proposalsTexts.clientTitle(),
            loading: isRefreshing,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: ProposalsDashboardTID.Empty(),
                illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                title: isVendor && proposalsTexts.emptyState.vendorTitle(),
                message: isVendor ? proposalsTexts.emptyState.vendorMessage() : proposalsTexts.emptyState.clientMessage(),
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            testId: "proposals-dashboard",
                            steps: [
                                {
                                    title: proposalsTexts.standardizedCreationTitle(),
                                    subtitle: proposalsTexts.emptyState.step1Subtitle(),
                                    image: {
                                        src: img1,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: proposalsTexts.productizedServicesTitle(),
                                    subtitle: proposalsTexts.emptyState.step2Subtitle(),
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: proposalsTexts.emptyState.step3Title(),
                                    subtitle: proposalsTexts.emptyState.step3Subtitle(),
                                    image: {
                                        src: img3,
                                        width: 1198,
                                        height: 700
                                    }
                                },
                                {
                                    title: proposalsTexts.trackingTitle(),
                                    subtitle: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "invertedStandard",
                                                children: proposalsTexts.emptyState.step4Subtitle1()
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "invertedStandard",
                                                children: proposalsTexts.emptyState.step4Subtitle2()
                                            })
                                        ]
                                    }),
                                    image: {
                                        src: img4,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            text: proposalsTexts.createProposal(),
                            testId: ProposalsDashboardTID.EmptyNewButton()
                        })
                    ]
                })
            })
        });
    }
    const openImportProposalsModal = ()=>{
        overlayContext.showModal({
            biName: 'proposals_dashboard_import_modal_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'proposals_confirm'
                        },
                        secondaryButton: {
                            name: 'proposals_cancel'
                        }
                    },
                    textAlignCenter: false,
                    primaryButtonLabel: importModalTexts.close(),
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: importModalTexts.title(),
                    primaryButtonTestId: ProposalsDashboardTID.CloseImportModalButton(),
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: importModalTexts.intro()
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        bi: {
                                            name: 'import_proposals_complete_form'
                                        },
                                        children: importModalTexts.completeForm()
                                    }),
                                    ' ',
                                    importModalTexts.outro()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: importModalTexts.note()
                            })
                        ]
                    })
                });
            }
        });
    };
    async function exportAllProposalsCsv() {
        if (exportingAllCsv) return;
        setExportingAllCsv(true);
        try {
            const csvString = await api.relationship.exportAllProposalsCsv();
            downloadCsv(csvString, "anchor_proposals_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            overlayContext.showErrorToast(proposalsTexts.exportError(), 'export-proposals-csv-error');
        } finally{
            setExportingAllCsv(false);
        }
    }
    async function bulkExportProposalsCsv() {
        if (bulkExportingCsv) return;
        setBulkExportingCsv(true);
        const ids = getSelectedProposals();
        try {
            const csvString = await api.relationship.exportProposalsCsvByIds(ids);
            downloadCsv(csvString, "anchor_proposals_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            overlayContext.showErrorToast(proposalsTexts.exportError(), 'export-proposals-csv-error');
        } finally{
            setBulkExportingCsv(false);
        }
    }
    async function bulkSendReminders(selectedProposals) {
        await doBulkAction({
            bulkItemIds: selectedProposals,
            confirmation: {
                title: proposalsTexts.sendRemindersInBulkTitle(),
                primaryButtonLabel: proposalsTexts.sendRemindersButton(),
                content: /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsxs(Typography, {
                        children: [
                            proposalsTexts.sendRemindersConfirmationPrefix(),
                            ' ',
                            /*#__PURE__*/ _jsx("strong", {
                                children: selectedProposals.length
                            }),
                            ' ',
                            proposalsTexts.sendRemindersConfirmationSuffix()
                        ]
                    })
                })
            },
            progressTitle: proposalsTexts.sendRemindersProgressTitle(),
            actionName: 'sent',
            itemName: proposalsTexts.sendRemindersItemName(),
            onConfirmed: ()=>{
                logBI(11255, {
                    count: selectedProposals.length
                });
            },
            canProcessItem: ()=>true,
            processSingleItem: (proposalId)=>handlePromise(api.relationship.sendProposalApprovalReminder(proposalId)),
            onDone: (failedProposalIds)=>{
                setSelected(failedProposalIds.reduce((acc, proposalId)=>_object_spread_props(_object_spread({}, acc), {
                        [proposalId]: true
                    }), {}));
            }
        });
    }
    function viewActivityLog(proposal) {
        overlayContext.showModal({
            biName: 'activity_log_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ActivityLogModal, {
                    relationshipId: proposal.id,
                    dismissModal: dismissModal
                });
            }
        });
    }
    async function viewProposal(proposal) {
        const worksAt = selectors.user.business(getState());
        const userEmail = selectors.user.email(getState());
        if (!worksAt || !userEmail) return;
        const loader = 'viewProposal';
        overlayContext.startLoader(loader);
        const result = await handlePromise(Promise.all([
            api.relationship.get(proposal.id),
            fetchAgreementStudioData()
        ]));
        overlayContext.stopLoader(loader);
        if (result.success) {
            const [relationship] = result.data;
            const proposalAsDraft = DraftModel.fromRelationship(relationship);
            setViewProposalMode({
                proposalAsDraft,
                isExpiredProposal: relationship.status === RelationshipStatus.Expired,
                duplicate: actions.getDuplicateProposalAction(proposal.id, {
                    isSampleProposal: proposal.isSample,
                    draftId: proposal.draftId
                }),
                copyLink: actions.getCopyProposalLinkAction(proposal),
                sendReminder: actions.getSendProposalReminderAction(proposal.id, proposal.isSample),
                edit: actions.getEditProposalAction(proposal, 'viewProposal'),
                cancel: {
                    text: proposalsTexts.withdraw(),
                    intent: Intent.CancelProposal,
                    disabled: false,
                    onClick: ()=>overlayContext.showModal({
                            biName: 'withdraw_modal',
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            content: (param)=>{
                                let { dismissModal } = param;
                                return /*#__PURE__*/ _jsx(WithdrawModal, {
                                    openedBy: "agreement-studio",
                                    relationshipId: relationship.id,
                                    draftId: relationship.draftId,
                                    dismissModal: dismissModal,
                                    onSuccess: refresh
                                });
                            }
                        })
                }
            });
        } else {
            overlayContext.showErrorToast(errorTexts.unexpected());
        }
    }
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.RelationshipCreatedFromScheduledDraft,
        callback: refresh
    });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.RelationshipApproved,
        callback: refresh
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Page, {
            testId: "proposals-dashboard",
            ref: scrollElRef,
            header: isVendor ? proposalsTexts.vendorTitle() : proposalsTexts.clientTitle(),
            actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                        target: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'open_proposals_more_actions'
                            },
                            skin: "primaryOutline",
                            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                            testId: ProposalsDashboardTID.MoreActionsButton(),
                            children: proposalsTexts.moreActions()
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                bi: {
                                    name: 'import_proposals'
                                },
                                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                                testId: ProposalsDashboardTID.ImportButton(),
                                onClick: openImportProposalsModal,
                                children: proposalsTexts.importProposals()
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                bi: {
                                    name: 'export_all_proposals'
                                },
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: ProposalsDashboardTID.ExportAllButton(),
                                disabled: exportingAllCsv,
                                intent: Intent.ExportProposalsToCsv,
                                onClick: exportAllProposalsCsv,
                                children: proposalsTexts.exportAll()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {})
                ]
            }),
            subheader: proposalsTexts.sentProposalCount({
                count: data.filter((proposal)=>proposal.status !== RelationshipStatus.Expired).length
            }),
            loading: isRefreshing,
            onScroll: onScroll,
            children: [
                !!viewProposalMode && /*#__PURE__*/ _jsx(AgreementStudioPage, {
                    disableDataFetching: true,
                    viewProposalMode: viewProposalMode,
                    returnRoute: routes.proposalsDashboard,
                    onClose: ()=>setViewProposalMode(null)
                }),
                /*#__PURE__*/ _jsxs(TableList.Provider, {
                    hasActionColumn: true,
                    columnDefs: columnDefs,
                    sort: sort,
                    onSortChange: onSortChange,
                    enableRowSelection: isVendor,
                    selectedRows: selected,
                    onSelectionChange: setSelected,
                    numRows: sortedAndFiltered.length,
                    numSelectedRows: numSelected,
                    onToggleAllSelection: onToggleAllSelection,
                    children: [
                        /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.filtersRow,
                                    children: [
                                        /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: "proposals-dashboard",
                                            search: {
                                                bi: {
                                                    name: 'proposals_search'
                                                },
                                                value: search,
                                                testId: 'proposals-dashboard-search',
                                                registerHotkey: true,
                                                onChange: (event)=>{
                                                    const searchStr = event.target.value;
                                                    changeUrlState({
                                                        search: searchStr
                                                    });
                                                    debouncedLogSearchToBI.onChange(searchStr);
                                                },
                                                onClear: ()=>{
                                                    changeUrlState({
                                                        search: ''
                                                    });
                                                    debouncedLogSearchToBI.onClear();
                                                }
                                            },
                                            filterLabels: filterLabels,
                                            filterConfig: tableFilterConfig,
                                            selectedFilters: tableSelectedFilters,
                                            setSelectedFilters: noop,
                                            onRefresh: handleTableFiltersRefresh,
                                            onClear: resetFilters,
                                            isClearDisabled: !filter.length && !search && !sentBy.length,
                                            mobile: {
                                                sortOptions
                                            }
                                        }),
                                        isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs("div", {
                                            className: styles.bulkActions,
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    testId: ProposalsDashboardTID.BulkCounter(),
                                                    children: numSelected > 0 ? proposalsTexts.selectedCount({
                                                        count: numSelected
                                                    }) : ''
                                                }),
                                                /*#__PURE__*/ _jsxs(DropdownMenu, {
                                                    onOpenChange: setBulkActionsOpen,
                                                    target: /*#__PURE__*/ _jsx(ChipButton, {
                                                        bi: {
                                                            name: 'proposals_bulk_actions'
                                                        },
                                                        testId: ProposalsDashboardTID.BulkActions(),
                                                        suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                        highlight: bulkActionsOpen,
                                                        disabled: numSelected === 0,
                                                        children: proposalsTexts.bulkActions()
                                                    }),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                            bi: {
                                                                name: 'proposals_dashboard_bulk_send_reminders'
                                                            },
                                                            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                                                            testId: ProposalsDashboardTID.BulkSendReminders(),
                                                            onClick: ()=>bulkSendReminders(getSelectedProposals()),
                                                            disabled: !isVendor,
                                                            children: proposalsTexts.sendReminders()
                                                        }),
                                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                            bi: {
                                                                name: 'proposals_dashboard_bulk_export_csv'
                                                            },
                                                            iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                                            testId: ProposalsDashboardTID.BulkExportCsv(),
                                                            disabled: bulkExportingCsv,
                                                            intent: Intent.ExportProposalsToCsv,
                                                            onClick: ()=>bulkExportProposalsCsv(),
                                                            children: proposalsTexts.bulkExport()
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    testId: "proposals-dashboard-table-header"
                                })
                            ]
                        }),
                        sortedAndFiltered.length > 0 ? /*#__PURE__*/ _jsx(_Fragment, {
                            children: paginatedData.map((proposal, index)=>/*#__PURE__*/ _jsx(ProposalsDashboardRow, {
                                    proposal: proposal,
                                    isVendor: isVendor,
                                    index: index,
                                    refresh: refresh,
                                    onViewActivityLog: viewActivityLog,
                                    onViewProposal: viewProposal,
                                    proposalActions: actions
                                }, proposal.id))
                        }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                            title: proposalsTexts.noMatchingFilters.title(),
                            message: proposalsTexts.noMatchingFilters.message(),
                            actions: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'reset_proposals_filters'
                                },
                                onClick: resetFilters,
                                children: proposalsTexts.removeAllFilters()
                            })
                        })
                    ]
                })
            ]
        })
    });
}
export function ProposalsDashboard() {
    const { proposals, isLoading, isFetching, refresh, error } = useProposals({
        refreshOnHookMount: true
    });
    const isVendor = useStore(selectors.user.isVendor);
    const { t: proposalsTexts } = useLocaleKeys('pages.proposalsDashboard');
    const title = isVendor ? proposalsTexts.vendorTitle() : proposalsTexts.clientTitle();
    useSetDocumentTitle(title);
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            title: title,
            onRefresh: refresh
        });
    } else if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: true
        });
    } else {
        return /*#__PURE__*/ _jsx(ActualProposalsDashboard, {
            data: proposals,
            isRefreshing: isFetching,
            refresh: refresh
        });
    }
}
